import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { FiHome, FiRefreshCw } from "react-icons/fi";
import { Md360 } from "react-icons/md";
import { centerSceneConfigs } from "../configs/centerSceneConfigs";
import "../assets/styles/viewer360.css";

function degToRad(deg) {
  return (deg * Math.PI) / 180;
}
function radToDeg(rad) {
  return (rad * 180) / Math.PI;
}

function CenterViewer360() {
  const { center } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const panoRef = useRef(null);
  const [viewInstance, setViewInstance] = useState(null);
  const [autoRotateInterval, setAutoRotateInterval] = useState(null);
  const thumbnailsRef = useRef(null);
  const activeThumbRef = useRef(null);

  useEffect(() => {
    const Marzipano = window.Marzipano;
    if (!Marzipano || !panoRef.current) return;

    const scene = centerSceneConfigs[center];
    if (!scene) {
      console.warn("Không tìm thấy cấu hình scene cho:", center);
      return;
    }

    const viewer = new Marzipano.Viewer(panoRef.current);
    const geometry = new Marzipano.EquirectGeometry([{ width: 6144 }]);

    const view = new Marzipano.RectilinearView(
      {
        yaw: degToRad(scene.view.yaw),
        pitch: degToRad(scene.view.pitch),
        fov: degToRad(scene.view.fov),
      },
      Marzipano.RectilinearView.limit.traditional(6144, degToRad(120))
    );

    const source = Marzipano.ImageUrlSource.fromString(scene.image);
    const sceneInstance = viewer.createScene({
      source,
      geometry,
      view,
      pinFirstLevel: true,
    });

    sceneInstance.switchTo();
    setViewInstance(view);

    setTimeout(() => {
      if (thumbnailsRef.current && activeThumbRef.current) {
        const container = thumbnailsRef.current;
        const active = activeThumbRef.current;
        const offset =
          active.offsetLeft -
          container.offsetWidth / 2 +
          active.offsetWidth / 2;
        container.scrollTo({ left: offset, behavior: "smooth" });
      }
    }, 300);

    const handleKeydown = (e) => {
      if (e.key.toLowerCase() === "l") {
        const yawDeg = radToDeg(view.yaw()).toFixed(2);
        const pitchDeg = radToDeg(view.pitch()).toFixed(2);
        const fovDeg = radToDeg(view.fov()).toFixed(2);
        console.log(`[${center}] View:\n`, {
          yaw: parseFloat(yawDeg),
          pitch: parseFloat(pitchDeg),
          fov: parseFloat(fovDeg),
        });
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const keys = Object.keys(centerSceneConfigs);
        const currentIndex = keys.indexOf(center);
        if (e.key === "ArrowLeft" && currentIndex > 0) {
          navigate(`/center/${keys[currentIndex - 1]}`);
        } else if (e.key === "ArrowRight" && currentIndex < keys.length - 1) {
          navigate(`/center/${keys[currentIndex + 1]}`);
        }
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => {
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [center, location.key, navigate]);

  const handleResetView = () => {
    const scene = centerSceneConfigs[center];
    if (viewInstance && scene) {
      viewInstance.setYaw(degToRad(scene.view.yaw));
      viewInstance.setPitch(degToRad(scene.view.pitch));
      viewInstance.setFov(degToRad(scene.view.fov));
    }
  };

  const handleToggleAutoRotate = () => {
    if (!viewInstance) return;
    if (autoRotateInterval) {
      clearInterval(autoRotateInterval);
      setAutoRotateInterval(null);
    } else {
      const interval = setInterval(() => {
        viewInstance.setYaw(viewInstance.yaw() + 0.002);
      }, 16);
      setAutoRotateInterval(interval);
    }
  };

  return (
    <div className="viewer360">
      <div
        className="viewer360__canvas"
        ref={panoRef}
      />

      <div className="viewer360__controls">
        <button
          onClick={() => navigate("/")}
          title="Trang chủ"
          className="viewer360__control-button"
        >
          <FiHome />
        </button>

        <button
          onClick={handleResetView}
          title="Reset góc nhìn"
          className="viewer360__control-button"
        >
          <FiRefreshCw />
        </button>

        <button
          onClick={handleToggleAutoRotate}
          title={autoRotateInterval ? "Dừng xoay" : "Tự xoay"}
          className="viewer360__control-button"
        >
          <Md360 />
        </button>
      </div>

      <div
        className="viewer360__scene-thumbnails"
        ref={thumbnailsRef}
      >
        {Object.entries(centerSceneConfigs).map(([key, scene]) => (
          <div
            key={key}
            ref={key === center ? activeThumbRef : null}
            className={`viewer360__thumb ${
              key === center ? "viewer360__thumb--active" : ""
            }`}
            onClick={() => navigate(`/center/${key}`)}
          >
            <img
              src={scene.image}
              alt={key}
            />
            <span>{key.replace(/-/g, " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CenterViewer360;
